import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuOverviewInfoRow} from '#/components/actionmenu/action-menu-overview';
import {
  ActionMenuContentSectionOverview,
  ActionMenuContentSectionOverviewBuy,
  ActionMenuContentSectionOverviewLimitBuy,
  ActionMenuContentSectionOverviewLimitSell,
  ActionMenuContentSectionOverviewSell,
} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {ActionMenuTransactionLayout} from '#/components/actionmenu/sections/transaction/action-menu-transaction-layout';
import {
  TransactionVariables,
  useTransactionFlowByType,
} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {FunctionComponent, ReactNode, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {TransactionType} from 'shareland-common';

const AmountDisplay: FunctionComponent<{
  children: {
    title: ReactNode;
    subtitle: ReactNode;
  };
}> = ({children: {title, subtitle}}) => (
  <div>
    <span className='block text-deepblue font-semibold text-3xl break-all text-center'>{title}</span>
    <span className='block text-white font-semibold text-xl break-all text-center'>{subtitle}</span>
  </div>
);

const LandTokenAmountDisplay: FunctionComponent<{value: number}> = ({value}) => {
  const {
    land: {tokenSymbol},
  } = useContext(ActionMenuContext);

  const {t} = useTranslation('translation');

  return (
    <AmountDisplay>
      {{
        title: t('pages.actionMenu.transaction.landTokenAmountDisplay.title', {amount: value}),
        subtitle: t('pages.actionMenu.transaction.landTokenAmountDisplay.subtitle', {landTokenSymbol: tokenSymbol}),
      }}
    </AmountDisplay>
  );
};

const StableAmountDisplay: FunctionComponent<{value: number}> = ({value}) => {
  const displayPref = useCoinDisplayPreferences();

  const {t} = useTranslation('translation');

  return (
    <AmountDisplay>
      {{
        title: t('pages.actionMenu.transaction.stableTokenAmountDisplay.title', {
          amount: displayPref.displayAmount(value),
        }),
        subtitle: t('pages.actionMenu.transaction.stableTokenAmountDisplay.subtitle', {
          currencySymbol: displayPref.symbol,
        }),
      }}
    </AmountDisplay>
  );
};

export const ActionMenuTransactionBuy: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.Purchase];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] = useTransactionFlowByType[TransactionType.Purchase](transactionVars);

  // Vars
  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.Purchase}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
          </div>
        ),
        overview: <ActionMenuContentSectionOverviewBuy transactionVars={transactionVars} />,
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionSell: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.Sell];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] = useTransactionFlowByType[TransactionType.Sell](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.Sell}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
          </div>
        ),
        overview: <ActionMenuContentSectionOverviewSell transactionVars={transactionVars} />,
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionRent: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.MintLiquidityToken];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.MintLiquidityToken](transactionVars);

  // Vars
  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.MintLiquidityToken}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.rentStableAmount} />
            </div>
          </div>
        ),
        overview: (
          <ActionMenuContentSectionOverview>
            <ActionMenuOverviewInfoRow.RentLand landTokenAmount={transactionVars.landTokenAmount} />
            <ActionMenuOverviewInfoRow.RentStable stableTokenAmount={transactionVars.rentStableAmount} />
            <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
            <ActionMenuOverviewInfoRow.TotalLandStable
              landTokenAmount={transactionVars.landTokenAmount}
              stableTokenAmount={transactionVars.rentStableAmountWithFee}
            />
            <ActionMenuOverviewInfoRow.RentReceive lpTokenAmount={transactionVars.lpTokenAmount} />
          </ActionMenuContentSectionOverview>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionStopRent: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.BurnLiquidityToken];
}> = ({transactionVars}) => {
  // Context
  const {landMarketInfo} = useContext(ActionMenuContext);

  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.BurnLiquidityToken](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.BurnLiquidityToken}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <AmountDisplay>
              {{
                title: transactionVars.lpTokenAmount,
                subtitle: landMarketInfo.lpSymbol,
              }}
            </AmountDisplay>
          </div>
        ),
        overview: (
          <ActionMenuContentSectionOverview>
            <ActionMenuOverviewInfoRow.Burn lpTokenAmount={transactionVars.lpTokenAmount} />
            <ActionMenuOverviewInfoRow.ReceiveLand landTokenAmount={transactionVars.landTokenAmount} />
            <ActionMenuOverviewInfoRow.ReceiveStable stableTokenAmount={transactionVars.stableTokenAmount} />
          </ActionMenuContentSectionOverview>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionBorrowOpen: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.OpenCollateralPosition];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.OpenCollateralPosition](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.OpenCollateralPosition}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.stableTokenAmount} />
            </div>
          </div>
        ),
        overview: (
          <ActionMenuContentSectionOverview>
            <ActionMenuOverviewInfoRow.Borrow landTokenAmount={transactionVars.landTokenAmount} />
            <ActionMenuOverviewInfoRow.Deposit stableTokenAmount={transactionVars.stableTokenAmount} />
            <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
            <ActionMenuOverviewInfoRow.Total total={transactionVars.totalStableAmount} />
            <ActionMenuOverviewInfoRow.CollateralRatio collateralRatio={transactionVars.collateralRatio} />
          </ActionMenuContentSectionOverview>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionBorrowAdjust: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.AdjustCollateralPosition];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.AdjustCollateralPosition](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.AdjustCollateralPosition}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.stableTokenAmount} />
            </div>
          </div>
        ),
        overview: (
          <ActionMenuContentSectionOverview>
            <ActionMenuOverviewInfoRow.Borrow landTokenAmount={transactionVars.landTokenAmount} />
            <ActionMenuOverviewInfoRow.Deposit stableTokenAmount={transactionVars.stableTokenAmount} />
            <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
            <ActionMenuOverviewInfoRow.Total total={transactionVars.totalStableAmount} />
            <ActionMenuOverviewInfoRow.CollateralRatio collateralRatio={transactionVars.collateralRatio} />
            <ActionMenuOverviewInfoRow.DifferenceFromLastPosition
              stableTokenAmount={transactionVars.stableAmountPositionDifference}
            />
          </ActionMenuContentSectionOverview>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionBorrowClose: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.CloseCollateralPosition];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.CloseCollateralPosition](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.CloseCollateralPosition}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.borrowedLandTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.stableTokenInCollateralAmount} />
            </div>
          </div>
        ),
        overview: (
          <ActionMenuContentSectionOverview>
            <ActionMenuOverviewInfoRow.GiveBack landTokenAmount={transactionVars.borrowedLandTokenAmount} />
            <ActionMenuOverviewInfoRow.Receive receive={transactionVars.stableTokenInCollateralAmount} />
          </ActionMenuContentSectionOverview>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionClaimRewards: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.ClaimRewards];
}> = ({transactionVars}) => {
  // External hooks
  const {t} = useTranslation('translation');

  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.ClaimRewards](transactionVars);

  const displayPref = useCoinDisplayPreferences();

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.ClaimRewards}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full h-full flex flex-col justify-center items-center gap-2'>
            <span className='text-white font-semibold text-3xl'>{t('actions.claim')}</span>
            <span className='text-deepblue font-semibold text-3xl'>
              {t('pages.actionMenu.rent.sections.claim.claimableAmount.value', {
                currencySymbol: displayPref.symbol,
                amount: displayPref.displayAmount(transactionVars.claimableStableAmount),
              })}
            </span>
          </div>
        ),
        overview: null,
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionLimitBuy: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.PurchaseLimitOrder];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.PurchaseLimitOrder](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.PurchaseLimitOrder}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.stableTokenAmount} />
            </div>
          </div>
        ),
        overview: (
          <>
            <ActionMenuContentSectionOverviewLimitBuy transactionVars={transactionVars} />
          </>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};

export const ActionMenuTransactionLimitSell: FunctionComponent<{
  transactionVars: TransactionVariables[TransactionType.SellLimitOrder];
}> = ({transactionVars}) => {
  // External hooks
  const [transactionFlow, startTransactionFlow] =
    useTransactionFlowByType[TransactionType.SellLimitOrder](transactionVars);

  return (
    <ActionMenuTransactionLayout
      transactionType={TransactionType.SellLimitOrder}
      transactionFlow={transactionFlow}
      startTransactionFlow={startTransactionFlow}
    >
      {{
        mainInfo: (
          <div className='w-full grow flex flex-col justify-center items-center gap-2'>
            <div>
              <LandTokenAmountDisplay value={transactionVars.landTokenAmount} />
              <VerticalSeparator my='my-2' />
              <StableAmountDisplay value={transactionVars.stableTokenAmount} />
            </div>
          </div>
        ),
        overview: (
          <>
            <ActionMenuContentSectionOverviewLimitSell transactionVars={transactionVars} />
          </>
        ),
      }}
    </ActionMenuTransactionLayout>
  );
};
